import { initializeApp, getApp, getApps } from 'firebase/app';
import {
  getMessaging, getToken, isSupported, Messaging,
} from 'firebase/messaging';

import appConfig from '@config/app';
import { handleError } from '@services/ErrorHandler';

const { firebase } = appConfig;

// Initialize Firebase app singleton
const app = !getApps().length ? initializeApp(firebase) : getApp();

// Global messaging instance
let messaging: Messaging | null = null;

/**
 * Lazily initialize Firebase Messaging with retry for deployed timing issues
 */
const initMessaging = async (attempt = 0): Promise<Messaging | null> => {
  if (typeof window === 'undefined' || !('Notification' in window) || !('serviceWorker' in navigator)) {
    return null;
  }

  try {
    const supported = await isSupported();
    if (!supported) return null;

    try {
      if (!messaging) {
        messaging = getMessaging(app);
      }

      return messaging;
    } catch (err: any) {
      if (err.message.includes('Service messaging is not available') && attempt < 3) {
        return initMessaging(attempt + 1); // recursive retry
      }
      throw err;
    }
  } catch (error) {
    handleError(error);
    return null;
  }
};

/**
 * Request Notification permission and get FCM token
 */
export const requestNotificationPermission = async (
  updateFcmToken: (token: string) => Promise<any> | void,
): Promise<string | null> => {
  if (typeof window === 'undefined' || !('Notification' in window) || !('serviceWorker' in navigator)) {
    return null;
  }

  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return null;

    const messagingInstance = await initMessaging();
    if (!messagingInstance) return null;

    const registration = await navigator.serviceWorker.ready;

    const token = await getToken(messagingInstance, {
      vapidKey: process.env.REACT_APP_FIREBASE_MESSAGING_KEY!,
      serviceWorkerRegistration: registration,
    });

    if (token) {
      await updateFcmToken(token);
    }
    return token || null;
  } catch (error) {
    handleError(error);
    return null;
  }
};

/**
 * Get the global messaging instance (nullable)
 */
export const getMessagingInstance = (): Messaging | null => messaging;
